import { api } from './apiClient';

/**
 * Period Close (Month-End Close) Control Center API.
 * The backend is the source of truth; types are intentionally light. Every
 * number returned traces to real posted data (readiness engine, migration 053).
 */

export interface PeriodRow {
  id: number; financial_year: string; period_number: number; period_name: string;
  start_date: string; end_date: string; is_closed: number;
  status?: string; readiness_pct?: number | null; label?: string;
  closed_at?: string | null; closed_by?: number | null;
}
export interface Domain { key: string; label: string; score: number; status: 'PASS' | 'WARN' | 'FAIL'; weight?: number; }
export interface ChecklistItem {
  category: string; check: string; description: string;
  status: 'PASS' | 'WARN' | 'FAIL'; blocking: boolean;
  count?: number; amount?: number; route: string;
}
export interface PeriodException {
  severity: 'BLOCKING' | 'WARNING' | 'INFO'; blocking: boolean;
  title: string; amount: number; detail: string; route: string;
}
export interface CloseSummary {
  currentPeriod: PeriodRow; status: string; closeReadiness: number;
  canClose: boolean; blockingCount: number;
  unpostedTransactions: number; unreconciledBankItems: number;
  openAR: number; openAP: number; taxItemsPending: number;
  gstNetPayable: number; journalsPending: number; domains: Domain[];
}

const params = (financialYear: string, periodNumber: number) => ({ params: { financialYear, periodNumber } });

export const periodCloseService = {
  listPeriods: (financialYear?: string): Promise<{ periods: PeriodRow[] }> =>
    api.get('/period-close/periods', { params: financialYear ? { financialYear } : undefined }),
  summary: (fy: string, pn: number): Promise<CloseSummary> => api.get('/period-close/summary', params(fy, pn)),
  readiness: (fy: string, pn: number): Promise<{ period: PeriodRow; readiness: number; domains: Domain[] }> =>
    api.get('/period-close/readiness', params(fy, pn)),
  checklist: (fy: string, pn: number): Promise<{ period: PeriodRow; items: ChecklistItem[]; byCategory: Record<string, ChecklistItem[]>; counts: any; canClose: boolean; blockers: string[] }> =>
    api.get('/period-close/checklist', params(fy, pn)),
  exceptions: (fy: string, pn: number): Promise<{ period: PeriodRow; exceptions: PeriodException[]; counts: { blocking: number; warning: number; info: number } }> =>
    api.get('/period-close/exceptions', params(fy, pn)),
  periodSummary: (fy: string, pn: number): Promise<{ period: PeriodRow; current: any; previous: any; deltas: any }> =>
    api.get('/period-close/period-summary', params(fy, pn)),
  period360: (fy: string, pn: number): Promise<any> => api.get('/period-close/360', params(fy, pn)),
  submit: (financialYear: string, periodNumber: number) => api.post('/period-close/submit', { financialYear, periodNumber }),
  approve: (financialYear: string, periodNumber: number, body?: { override?: boolean; reason?: string }) =>
    api.post('/period-close/approve', { financialYear, periodNumber, ...body }),
  close: (financialYear: string, periodNumber: number, body?: { override?: boolean; reason?: string }) =>
    api.post('/period-close/close', { financialYear, periodNumber, ...body }),
  reopen: (financialYear: string, periodNumber: number, reason: string) =>
    api.post('/period-close/reopen', { financialYear, periodNumber, reason }),
};

export default periodCloseService;
